import { IconFilter } from '@tabler/icons-react';
import { use } from 'react';

import { NO_POS } from '#core/lib/expr/ast.ts';
import { type AstInput, isOpCall } from '#core/lib/expr/normalize.ts';
import { describeColumnType } from '#core/lib/expr/schema/types.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import { expectDataset } from '#core/lib/expr/value.ts';

import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import {
  type TransformDatasetInput,
  TransformDatasetInputSchema,
  type TransformDatasetOutput,
} from '#plugin-chat/components/tools/transform-dataset/schema.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { Expr, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { DatasetPreview } from '#plugin-chat/lib/tool/DatasetPreview.tsx';
import { ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { summarizeWithLocalRecovery } from '#plugin-chat/lib/tool/recovery.ts';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';
import { expressionArgsIdentity, expressionToAst } from '#plugin-chat/lib/tool/tool-identity.ts';
import { useThreadScopedEval } from '#plugin-chat/session/useThreadScopedEval.ts';

/**
 * Ops that only reshape the row SCHEMA — never the row set or any cell value.
 * A transform built solely from these over a single upstream binding is
 * information-preserving: it can be folded into the next op instead of being
 * bound as its own throwaway dataset.
 */
const PROJECTION_OPS: ReadonlySet<string> = new Set(['rename', 'columns', 'select']);

/**
 * If `expression` is a pure projection chain over a single bound dataset
 * (only {@link PROJECTION_OPS}, walking the thread-first chain down to a bare
 * binding reference — no filter / aggregate / join / source op anywhere),
 * return the ordered op names (upstream → top); otherwise `null`.
 *
 * Drives the `inlineable-projection` dataset signal that nudges the agent to
 * compose rather than stage a noop binding — the churn pattern where a rename
 * or column-narrowing is bound as its own dataset before the real op.
 */
const inlineableProjectionOps = (expression: string): readonly string[] | null => {
  let node: AstInput;
  try {
    node = expressionToAst(expression);
  } catch {
    return null;
  }
  const ops: string[] = [];
  // Thread-first desugar nests the upstream at params[0]; walk down the chain
  // collecting projections until we hit a non-projection op or the seed.
  while (isOpCall(node) && PROJECTION_OPS.has(node.name)) {
    ops.unshift(node.name);
    node = node.params?.[0] ?? null;
  }
  // Inlineable only if we saw at least one projection AND the upstream is a
  // bare binding (not another op still doing real work below the projections).
  return ops.length > 0 && !isOpCall(node) ? ops : null;
};

/**
 * TransformDatasetTool — s-expr-driven dataset transform.
 *
 * The LLM writes an s-expression — typically a top-level transform op
 * like `(filter <bound-dataset-name> #(<predicate>))` /
 * `(aggregate <bound-dataset-name> …)`, or a threaded pipeline
 * `(-> <bound-dataset-name> (filter …) (sort …))`, where
 * `<bound-dataset-name>` is the binding name of any dataset produced
 * by an earlier tool call on this branch — that evaluates to a
 * `Dataset`. `execute` evaluates the expression (cheap — op dispatch
 * appends plan nodes to the LazyDataset, no I/O). The cross-turn
 * namespace is a pure projection of the active message branch: this
 * tool-call part *is* the binding under `args.outputDataset` — a
 * downstream `(col <outputDataset> …)` re-derives the value by
 * re-evaluating this expression, scoped to the producers before it.
 * Nothing is staged, committed, or persisted separately; a
 * failed/streaming call simply isn't a producer. Pick a unique
 * `outputDataset` (if a name is reused on a branch the last successful
 * producer wins). The LLM gets a schema-summary envelope back
 * synchronously so it can chain further tool calls without waiting for
 * materialisation.
 *
 * `render` calls `useMaterializedExpr(args.expression)` to force the
 * dataset on demand for the visible chrome; sibling renders for the
 * same expression share TanStack's Suspense cache.
 *
 * No factory props, no per-call EvalContext assembly —
 * `<ChatSessionProvider>` populates `EvalDepsContext` once for the
 * whole conversation and every hook below reads from it.
 *
 * The full s-expression grammar (top-level transform ops, agg-context
 * funcs, column primitives) is documented once in the shared
 * `<ExprDslReference>` system-prompt block (see `AtlasInstructions`),
 * not re-embedded per tool.
 *
 * Strategic LLM guidance lives on the `description` prop via a nested
 * `<ToolGuidance>` — `<Tool>` has no separate `instructions` channel.
 * The guidance covers: discover-distinct-values-before-filtering,
 * aggregate-never-sample, the timeseries-from-a-ref-column idioms
 * (`unnest` for raw points, `time-bucket` for aggregated buckets),
 * wide-shaping for charts, and the `join` op.
 */
export function TransformDatasetTool(props: ToolProps) {
  const scoped = useThreadScopedEval();
  const materialize = useMaterialize();
  const tools = use(ToolsContext);

  return (
    <Tool<TransformDatasetInput, TransformDatasetOutput>
      collapsible
      color="blue"
      description={
        <ToolGuidance
          description="Run an s-expression transformation over a previously-bound dataset and bind the result under a new name."
          whenNotToUse={[
            'Fetching instance data from the knowledge graph (use `queryKnowledgeGraph`)',
            'Presenting results to the user (use `showDashboard`)',
          ]}
          whenToUse={[
            'Filtering, deriving, sorting, slicing, aggregating, joining, or reshaping a previously-bound dataset.',
            'Fetching/aggregating timeseries datapoints from a `timeseries<number><CogniteTimeSeries>` column produced by `queryKnowledgeGraph`.',
            'Discovering the value domain of a categorical column before filtering it — run `(aggregate ds :group-by [:<col>] :pairs {:n (count)})` to reveal the actual distinct values and frequencies. Prefer this over guessing a filter value that may return 0 matches with no indication the guess was wrong.',
            'Answering any "how many X" / sum / mean / min/max / distribution question — must go through an `(aggregate …)`, never inferred from a row sample. Prefer grouping without a pre-applied filter.',
            'Combining two bound datasets on a shared key (the `join` op) before further processing.',
            'Preparing a dataset for `showDashboard`.',
          ]}
        >
          <p>
            Any s-expression returning a Dataset is accepted. Each top-level transform op (<code>filter</code>,{' '}
            <code>derive</code>, <code>sort</code>, <code>aggregate</code>, <code>join</code>, …) takes the dataset as
            its first arg; chain steps via the threading macro <code>(-&gt; ds (filter …) (sort …))</code>. Datasets are
            lazy by default; rows materialise on demand. Op grammar (signatures, kwargs, agg-fns) is in the DSL
            reference; the guidance below is strategic only.
          </p>
          <example name="transform-then-dashboard">
            <p>User: "Total revenue per region for the wells I just queried."</p>
            <ol>
              <li>
                <code>transformDataset</code> — expression{' '}
                <Expr>{'(aggregate top-wells :group-by [:region] :pairs {:revenue (sum :revenue)})'}</Expr>,
                outputDataset <code>revenue-by-region</code> → binds <code>revenue-by-region</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <Expr>{`(dashboard :title "Revenue by region" (cartesian (series :type bar :id rev :label "Revenue" :color blue :x (col revenue-by-region region) :y (col revenue-by-region revenue)) :span [12 6]))`}</Expr>
              </li>
            </ol>
          </example>

          <h3>Compose ops; don't bind throwaway datasets</h3>
          <p>
            Chain steps in one expression with <code>(-&gt; ds (filter …) (sort …))</code> rather than binding a
            separate dataset per step. A column narrowing (<code>columns</code>) or a <code>rename</code> changes no
            rows — fold it into the next op instead of materialising it on its own. Bind a new dataset only when you
            will reference it more than once, or it is a genuine branch point. A result tagged{' '}
            <em>inlineable-projection</em> is the signal that you bound a noop.
          </p>

          <h3>Discover distinct values before filtering</h3>
          <p>
            Before filtering on an unfamiliar categorical column, run an aggregate with a count to reveal the actual
            distinct values and their frequencies:{' '}
            <code>(aggregate wells :group-by [:region] :pairs {'{:n (count)}'})</code>. Filtering on a guessed value
            that does not exist returns 0 rows with no signal the guess was wrong.
          </p>

          <h3>Aggregate, never sample</h3>
          <p>
            Any "how many" / total / mean / min / max / distribution answer MUST come from an <code>aggregate</code> —
            never inferred from a row preview (there is none in the response by design). Omit <code>:group-by</code> for
            a global 1-row rollup (the canonical shape to feed a <code>showDashboard</code> <code>metric</code>). Drop
            nulls before aggregating/sorting/ranking — null is a gap, not zero: put{' '}
            <code>(filter ds #(is-not-null (:col %)))</code> (or{' '}
            <code>(filter ds #(and (is-not-null (:a %)) (is-not-null (:b %))))</code> for several) immediately after the
            resolving <code>unnest</code>.
          </p>
          <h3>Aggregation keeps only group keys and pair outputs</h3>
          <p>
            An <code>aggregate</code> collapses each group to one row holding exactly the <code>:group-by</code> columns
            and the <code>:pairs</code> outputs — every other column is dropped (a non-grouped column has many values
            per group, so there is nothing to keep unless you pick one). You therefore cannot regroup a summary by a
            column it never retained; that yields <code>unknown column: X</code>. Keep a dimension available one of two
            ways:
          </p>
          <ul>
            <li>
              <strong>Group by it up front.</strong> If you will break results down by X later, include X in the{' '}
              <em>first</em> aggregate's <code>:group-by</code> (deriving its scalar subfield first when X is a struct
              column) rather than aggregating to a coarser key and regrouping.
            </li>
            <li>
              <strong>Carry it explicitly.</strong> Add <code>(first :X)</code> (or <code>max</code> / <code>min</code>{' '}
              / …) to <code>:pairs</code> to retain one representative value of X per group.
            </li>
          </ul>
          <p>
            To add a dimension a summary already dropped, rebuild from the base dataset that still has it — regrouping
            the base is more robust than joining the dimension back onto a collapsed rollup.
          </p>
          <h3>Timeseries from a ref column</h3>
          <ul>
            <li>
              <strong>Raw, unaggregated points:</strong> <code>unnest</code> the ref column — one row per (original-row,
              sample). To window raw points, <code>unnest</code> then <code>filter</code> on the timestamp.
            </li>
            <li>
              <strong>Aggregated buckets (charts):</strong> <code>time-bucket</code>. Use only for buckets, never raw
              samples. A per-cell <code>time-bucket</code> nests inner columns named{' '}
              <code>&lt;col&gt;-&lt;agg&gt;</code> (e.g. <code>oilRef-mean</code>) — always follow with{' '}
              <code>(unnest [cols…] :as [day …])</code> and reference the renamed names, never the bare original column.
              <code>:window</code> is half-open <code>[start end)</code>.
            </li>
            <li>
              <strong>Latest scalar per row (deterministic):</strong> per-cell <code>time-bucket</code> with{' '}
              <code>:agg [:last]</code> → <code>unnest</code> → null-filter → <code>sort</code> newest-first →{' '}
              <code>aggregate</code> with <code>(first :col)</code> — timestamp-ordered by construction and carries an
              explicit <code>as_of</code> (the {tools.QueryKnowledgeGraph} worked example shows the full pipeline). The
              terser <code>(aggregate :group-by [:key] :pairs {'{:out (value)}'})</code> is <strong>positional</strong>{' '}
              (last present datapoint in fetch order, not a timestamp-sorted "latest") and yields{' '}
              <strong>
                no <code>as_of</code>
              </strong>{' '}
              — prefer the explicit form whenever correctness or a date matters. There is no per-row scalar{' '}
              <code>derive</code> for a window-mean; use <code>time-bucket</code>/<code>unnest</code> +{' '}
              <code>aggregate</code>.
            </li>
          </ul>

          <h3>
            <code>columns</code> projects scalar columns only — never a ref / nested-dataset column
          </h3>
          <p>
            <code>(columns […])</code> narrows the row schema to its column list. Mentioning a{' '}
            <code>ref&lt;timeseries&gt;</code> / <code>nested-dataset</code> column (any tag-relation column) inside
            that list trips per-cell dispatch and corrupts the cell's inner schema, surfacing later as a{' '}
            <strong>materialised schema does not refine the declared schema</strong> error. To narrow a payload, list
            only scalar columns (<code>externalId</code>, names, units, plain numerics). To read the timeseries value,
            feed the ref column straight into <code>time-bucket</code> / <code>unnest</code> — no projection required
            first; the ref column is already on the row from the <code>queryKnowledgeGraph</code> output.
          </p>
          <h3>Sparse timeseries: fill the gaps</h3>
          <p>
            Sparse series leave <strong>null gap buckets</strong> after <code>time-bucket</code>.{' '}
            <code>:fill ffill</code> (or <code>bfill</code>) on the <code>time-bucket</code> call fills gap buckets
            within each series in one step. For already-flat data (after <code>unnest</code>, or any non-timeseries
            dataset) use the dedicated fill verbs from the DSL reference.{' '}
            <strong>Leading/trailing gaps with no source stay null</strong>; observed values are never overwritten.
          </p>
          <h3>Charting timeseries: shape it wide first</h3>
          <p>
            <code>showDashboard</code> binds one column per chart series. After <code>time-bucket</code> you have one
            bucketed column per metric (1×M — chart directly). For N entities × 1 metric, the data is{' '}
            <strong>long</strong> (one row per (timestamp, entity)); reshape to <strong>wide</strong> with{' '}
            <code>
              (pivot ds :rows [:&lt;id&gt;] :cols [:&lt;entityCol&gt;] :values (&lt;agg&gt; :&lt;valueCol&gt;))
            </code>{' '}
            so each entity becomes its own column, then point each{' '}
            <code>(series … :y (col &lt;ds&gt; &lt;entityCol&gt;))</code> at a distinct column — two series cannot share
            one column. When the consumer is a time-axis chart, produce one row per timestamp plus one numeric column
            per series and pass <code>(col &lt;ds&gt; timestamp)</code> as the series <code>:x</code>.
          </p>

          <h3>Relation / struct columns are not sort, join, or group keys</h3>
          <p>
            A direct-relation or reference column (e.g. <code>wellString</code>) has a <strong>struct</strong> type like{' '}
            <code>struct{'{ space, externalId, name? }'}</code> — it identifies a linked instance and has{' '}
            <strong>no scalar order</strong>, so it cannot be a <code>sort</code>, <code>join</code> <code>:on</code>,
            or <code>:group-by</code> key directly. Key on one of its scalar subfields (<code>externalId</code>,{' '}
            <code>name</code>) instead — but the syntax differs per op:
          </p>
          <ul>
            <li>
              <strong>
                <code>join</code> <code>:on</code>
              </strong>{' '}
              — a <code>(col … subfield)</code> ref <em>is</em> a valid key; pair it per side:{' '}
              <Expr>(join a b :on [[(col wellString externalId) (col wellRef externalId)]])</Expr> (the outer/inner
              position picks the side, not a binding prefix).
            </li>
            <li>
              <strong>
                <code>sort</code>
              </strong>{' '}
              — a bare <code>(col …)</code> is <em>not</em> a sort key; use a computed key{' '}
              <Expr>(sort ds [#(col % wellString name) :desc])</Expr> (or <code>derive</code> a scalar first).
            </li>
            <li>
              <strong>
                <code>:group-by</code> / <code>pivot</code>
              </strong>{' '}
              — <code>derive</code> the subfield to a scalar column, then key on that:{' '}
              <Expr>
                {
                  '(-> ds (derive :well #(col % wellString externalId)) (aggregate :group-by [:well] :pairs {:n (count)}))'
                }
              </Expr>
              .
            </li>
          </ul>
          <p>
            <strong>Renaming the column does not change its type.</strong> A renamed struct is still a struct, so a{' '}
            <code>rename</code> followed by the same <code>sort</code>/<code>join</code> fails identically (
            <code>well_name</code>, <code>well_name_str</code>, <code>well_name_txt</code> all behave exactly like{' '}
            <code>wellString</code>). When an op rejects a column as a struct or "no natural order", change the{' '}
            <em>reference</em> to a scalar subfield — never the column's name.
          </p>

          <h3>Joining two datasets</h3>
          <p>
            The <code>join</code> op (see DSL reference for <code>:on</code> entry kinds and <code>:type</code> values)
            silently renames non-key columns present on BOTH sides to <code>name_1</code> / <code>name_2</code> — the
            bare name no longer exists. So a dataset you join in only for a value should{' '}
            <code>aggregate :by [&lt;join-key&gt;]</code> alone (carry just the key + the value(s)), or{' '}
            <code>rename</code> the clashing column before the join. (<code>aggregate</code> outputs keep their exact
            binding names — no <code>_mean</code>/<code>_max</code> suffix.) For any further
            filter/derive/sort/aggregate on the joined result, chain another <code>transformDataset</code> on this
            tool's <code>outputDataset</code>.
          </p>
        </ToolGuidance>
      }
      errorResponseProps={() => ({
        flowSignal: {
          appendMessages: [
            {
              role: 'user',
              content: `Fix the error as described in the error message and call ${tools.TransformDataset} again with corrected parameters.

If you're unable to recover from the error, abort and inform the user using ${tools.CreateResponse} with "error" kind. Provide a concise, plain-text explanation of the error. Do not include any raw error messages, stack traces, or internal identifiers in the user-facing message unless the user has explicitly asked for it. Instead, translate them into clear, understandable language that conveys the nature of the problem and any relevant context. If possible, suggest next steps or alternative actions the user can take to resolve the issue.`,
            },
          ],
          narrowTools: [
            // tools.DescribeViews,
            tools.TransformDataset,
            tools.QueryKnowledgeGraph,
            // tools.ShowDashboard,
            // tools.AskUserQuestion,
            tools.CreateResponse,
          ],
          continuation: 'continue',
        },
      })}
      execute={async (args: TransformDatasetInput): Promise<TransformDatasetOutput> => {
        // Op dispatch appends plan nodes to the LazyDataset chain.
        // `summarizeWithLocalRecovery` materialises + summarises, and on
        // an empty/all-null result mines the expression's seed binding —
        // already in the session fetch cache from its producing turn,
        // never a fresh CDF fetch — for the value domain (distinct
        // values, ranges, closeMatches typo hints).
        // No store write — this tool-call part is itself the binding.
        const { evaluate } = scoped();
        const value = await evaluate(args.expression);
        const ds = expectDataset(value, 'transformDataset result', NO_POS);
        const { mat, summary } = await summarizeWithLocalRecovery({
          ds,
          expression: args.expression,
          evaluate,
          materialize,
        });
        // Flag a pure projection chain (rename/columns/select over a bound
        // dataset) so the agent folds it into the next op instead of binding
        // a throwaway dataset. Purely a hint — the materialised result is
        // unchanged.
        const inlineableOps = inlineableProjectionOps(args.expression);
        const summaryWithHints = inlineableOps ? { ...summary, inlineableOps } : summary;
        return {
          success: true,
          state: mat.state,
          outputDataset: args.outputDataset,
          schema: Array.from(ds.schema.entries()).map(([name, type]) => ({
            name,
            kind: describeColumnType(type),
          })),
          summary: summaryWithHints,
          flowSignal: {
            narrowTools: [
              tools.DescribeViews,
              tools.TransformDataset,
              tools.QueryKnowledgeGraph,
              tools.ShowDashboard,
              tools.AskUserQuestion,
              tools.CreateResponse,
            ],
            continuation: 'continue',
          },
        };
      }}
      icon={IconFilter}
      identityKey={expressionArgsIdentity}
      message={{
        call: ({ args }) => (
          <p>
            Transforming → <code>{args.outputDataset}</code>: <code>{args.expression}</code>
          </p>
        ),
        result: ({ output }) => (
          <DatasetPreview binding={output.outputDataset} state={output.state} summary={output.summary} />
        ),
        error: ({ args, envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            recovery={
              <p>
                Expression <code>{args.expression}</code>. Confirm the referenced binding(s) exist on this branch and
                the op/columns are valid, then retry.
              </p>
            }
            toolName={toolName}
          />
        ),
      }}
      parameters={TransformDatasetInputSchema}
      {...props}
    />
  );
}
